import apiClient from "./apiClient";
import { API_ENDPOINTS } from "../config/api";

/**
 * ai-usage-billing: per-tenant AI usage (invoicing basis) + quota config.
 * Mirrors backend platform-tenant.service TenantAiConfig/TenantAiUsage.
 */

export interface TenantAiConfig {
  tenantId: string;
  aiBillingMode: "TRIAL" | "STANDARD";
  aiMonthlyTokenLimit: number | null;
  aiMonthlyImageLimit: number | null;
}

export interface UpdateTenantAiConfigDto {
  aiBillingMode?: "TRIAL" | "STANDARD";
  aiMonthlyTokenLimit?: number | null;
  aiMonthlyImageLimit?: number | null;
}

export interface TenantAiUsageMonth {
  /** "YYYY-MM" (UTC). Index 0 = current month. */
  month: string;
  tokens: number;
  images: number;
}

export interface TenantAiUsage {
  tenantId: string;
  months: TenantAiUsageMonth[];
  limits: {
    tokenLimit: number;
    imageLimit: number;
    tokenLimitIsDefault: boolean;
    imageLimitIsDefault: boolean;
  };
  currentMonth: {
    tokens: number;
    images: number;
    tokenRatio: number;
    imageRatio: number;
  };
  environmentsIncluded: string[];
}

export const aiUsageService = {
  async getAiConfig(tenantId: string): Promise<TenantAiConfig> {
    const response = await apiClient.get<TenantAiConfig>(
      `${API_ENDPOINTS.TENANTS}/${tenantId}/ai-config`,
    );
    return response.data;
  },

  async updateAiConfig(
    tenantId: string,
    dto: UpdateTenantAiConfigDto,
  ): Promise<TenantAiConfig> {
    const response = await apiClient.patch<TenantAiConfig>(
      `${API_ENDPOINTS.TENANTS}/${tenantId}/ai-config`,
      dto,
    );
    return response.data;
  },

  async getAiUsage(tenantId: string, months = 6): Promise<TenantAiUsage> {
    const response = await apiClient.get<TenantAiUsage>(
      `${API_ENDPOINTS.TENANTS}/${tenantId}/ai-usage`,
      { params: { months } },
    );
    return response.data;
  },
};
